import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Loader2, Clock } from "lucide-react";
import { COMPANY } from "../data";
import { api } from "../lib/api";
import { SmsConsentCheckbox } from "../components/SmsConsentCheckbox";

const ease = [0.16, 1, 0.3, 1];

export function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    inquiry_type: "client", message: "",
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, sms_consent: smsConsent });
      toast.success("Message sent. We'll respond within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", inquiry_type: "client", message: "" });
      setSmsConsent(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send";
      toast.error(typeof msg === "string" ? msg : "Failed to send");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="contact-page" className="page-enter pt-32 sm:pt-40">
      <section className="container-x pb-14">
        <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-4">Contact</p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease }}
          className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-balance max-w-5xl text-[#0F1E3C]"
        >
          Tell us what
          <br />
          <span className="italic font-serif gradient-text-amber">you&rsquo;re building.</span>
        </motion.h1>
      </section>

      <section className="container-x pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <p className="text-lg text-[#475569] leading-relaxed mb-4">
              Whether you&rsquo;re hiring for one role or scaling an entire department,
              we&rsquo;d love to hear from you. Share a few details and we&rsquo;ll get back
              within one business day.
            </p>
            <p className="text-xs text-[#64748B] mb-10">
              Albany Staffing is a <strong className="text-[#0F1E3C]">DBA of Albany Systems LLC</strong>.
            </p>

            <div className="space-y-5">
              <InfoRow icon={<MapPin className="w-5 h-5" />} label="Visit" value={COMPANY.address} />
              <InfoRow icon={<Mail className="w-5 h-5" />} label="Email"
                value={<a href={`mailto:${COMPANY.email}`} className="hover:text-[#E9610E]">{COMPANY.email}</a>} />
              <InfoRow icon={<Clock className="w-5 h-5" />} label="Hours"
                value={<>Mon–Fri · 8:00 AM – 6:00 PM CT</>} />
            </div>

            <div className="mt-10 card-soft rounded-2xl p-6 bg-white">
              <p className="text-xs uppercase tracking-[0.15em] text-[#E9610E] font-semibold mb-3">Need Help?</p>
              <p className="text-sm text-[#475569] leading-relaxed">
                For urgent requests, email us directly at{" "}
                <a href={`mailto:${COMPANY.email}`} className="text-[#0F1E3C] font-semibold hover:text-[#E9610E]">
                  {COMPANY.email}
                </a>{" "}
                — we read and respond to every inquiry personally.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 card-soft rounded-3xl p-8 sm:p-10 bg-white">
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="flex gap-2 p-1 border border-[#EAE3D2] rounded-full w-fit bg-[#FAF7F2]">
                {[
                  { id: "client", label: "I'm hiring" },
                  { id: "candidate", label: "I'm looking" },
                  { id: "partnership", label: "Partnership" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`inquiry-type-${opt.id}`}
                    onClick={() => setForm({ ...form, inquiry_type: opt.id })}
                    className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                      form.inquiry_type === opt.id
                        ? "bg-[#0F1E3C] text-white"
                        : "text-[#475569] hover:text-[#0F1E3C]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <Field label="Name" required>
                <input data-testid="contact-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Email" required>
                  <input data-testid="contact-email-input" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" />
                </Field>
                <Field label="Phone">
                  <input data-testid="contact-phone-input" type="tel" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
                </Field>
              </div>
              <Field label="Company">
                <input data-testid="contact-company-input" value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })} className="input" />
              </Field>
              <Field label="How can we help?" required>
                <textarea data-testid="contact-message-input" rows="5" required value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} className="input resize-none" />
              </Field>

              <div className="pt-2">
                <SmsConsentCheckbox checked={smsConsent} onChange={setSmsConsent} testId="contact-sms-consent" />
              </div>

              <p className="text-xs text-[#64748B] leading-relaxed">
                By submitting, you agree to be contacted by Albany Staffing LLC regarding your inquiry.
                See our{" "}
                <a href="/privacy" className="underline text-[#E9610E] hover:text-[#0F1E3C]">Privacy Policy</a>{" "}
                and{" "}
                <a href="/sms-terms" className="underline text-[#E9610E] hover:text-[#0F1E3C]">SMS Terms</a>.
              </p>

              <button type="submit" data-testid="contact-submit-btn" disabled={loading} className="btn-accent w-full">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send message <ArrowUpRight className="w-4 h-4" /></>}
              </button>

              <style>{`
                .input {
                  width: 100%;
                  background: #FFFFFF;
                  border: 1px solid #EAE3D2;
                  border-radius: 12px;
                  padding: 12px 16px;
                  color: #0F1E3C;
                  font-size: 14px;
                  transition: border-color 0.2s, box-shadow 0.2s;
                }
                .input:focus {
                  outline: none;
                  border-color: #E9610E;
                  box-shadow: 0 0 0 3px rgba(233,97,14,0.1);
                }
              `}</style>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#FFF4EA] flex items-center justify-center text-[#E9610E] shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[#64748B] font-semibold mb-1">{label}</div>
        <div className="text-base text-[#0F1E3C] font-medium">{value}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.1em] text-[#64748B] font-semibold block mb-2">
        {label}
        {required && <span className="text-[#E9610E] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
